import type {
  DashboardPageRecord,
  DashboardRecord,
  WidgetDataResolveResponse,
  WidgetDataResult,
} from "@playbookai/types";
import type { WidgetLayoutItemInput } from "./api.js";

/**
 * DashboardRecord → `EntityDashboard` adapters. The DS `DashboardItem` view
 * model is data-blind (instanceId/kind/width/params/title); the hosts compose
 * it from the page's ordered widget instances.
 */

export interface DashboardLayoutItem {
  readonly instanceId: string;
  readonly kind: DashboardPageRecord["widgetInstances"][number]["widgetKind"];
  readonly width: DashboardPageRecord["widgetInstances"][number]["width"];
  readonly params: Record<string, unknown>;
  readonly title?: string;
}

export function defaultPageOf(
  record: DashboardRecord,
): DashboardPageRecord | undefined {
  return record.pages.find((page) => page.isDefault) ?? record.pages[0];
}

export function pageToLayout(page: DashboardPageRecord): DashboardLayoutItem[] {
  return [...page.widgetInstances]
    .sort((a, b) => a.displayOrder - b.displayOrder)
    .map((instance) => ({
      instanceId: instance.id,
      kind: instance.widgetKind,
      width: instance.width,
      params: instance.params,
      ...(instance.titleOverride !== null
        ? { title: instance.titleOverride }
        : {}),
    }));
}

/**
 * Build the complete widget-layout batch (§11.3) from an ordered item list —
 * one commit, never one request per widget.
 */
export function layoutToBatchItems(
  items: readonly Pick<DashboardLayoutItem, "instanceId" | "width">[],
): WidgetLayoutItemInput[] {
  return items.map((item, index) => ({
    widgetInstanceId: item.instanceId,
    displayOrder: index,
    width: item.width,
  }));
}

/** One slot's resolution for one instance, if present. */
export function slotResult(
  response: WidgetDataResolveResponse | undefined,
  widgetInstanceId: string,
  slotKey: string,
): WidgetDataResult | undefined {
  return response?.results[widgetInstanceId]?.[slotKey];
}

/** The single connection bound to a slot on an instance, if any. */
export function connectionForSlot(
  page: DashboardPageRecord,
  widgetInstanceId: string,
  slotKey: string,
):
  | DashboardPageRecord["widgetInstances"][number]["connections"][number]
  | undefined {
  const instance = page.widgetInstances.find((i) => i.id === widgetInstanceId);
  return instance?.connections.find((c) => c.slotKey === slotKey);
}
